import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Link, TextField } from '@mui/material';
import { ApiError } from '../../../../lib/http/ApiError';
import { authApi } from '../../authApi';
import { validateLoginForm, isLoginFormValid, type LoginFormErrors } from '../../authValidation';
import type { LoginPayload } from '../../authTypes';
import { useAuth } from '../../hooks/useAuth';
import { AUTH_ROUTES } from '../../authConstants';

export function LoginForm() {
  const { t } = useTranslation('common');
  const { login } = useAuth();
  const [values, setValues] = useState<LoginPayload>({ username: '', password: '' });
  const [errors, setErrors] = useState<LoginFormErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (field: keyof LoginPayload) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues(prev => ({ ...prev, [field]: e.target.value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
    setServerError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formErrors = validateLoginForm(values);
    if (!isLoginFormValid(formErrors)) {
      setErrors(formErrors);
      return;
    }

    try {
      setLoading(true);
      setServerError(null);
      const { access_token, user } = await authApi.login(values);
      await login(access_token, user.username);
    } catch (err) {
      if (ApiError.isApiError(err) && (err.isValidationError || err.isUnauthorized)) {
        setServerError(t('auth.login.invalidCredentials'));
      } else if (
        ApiError.isApiError(err) &&
        err.statusCode === 403 &&
        (err.messages[0] ?? '').toLowerCase().includes('inactive')
      ) {
        setServerError(t('auth.login.accountInactive'));
      } else {
        setServerError(t('auth.login.serverError'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
      {serverError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {serverError}
        </Alert>
      )}

      <TextField
        label={t('auth.login.fields.username')}
        name="username"
        autoComplete="username"
        autoFocus
        fullWidth
        required
        margin="normal"
        value={values.username}
        onChange={handleChange('username')}
        error={Boolean(errors.username)}
        helperText={errors.username ? t(errors.username) : undefined}
        disabled={loading}
      />

      <TextField
        label={t('auth.login.fields.password')}
        name="password"
        type="password"
        autoComplete="current-password"
        fullWidth
        required
        margin="normal"
        value={values.password}
        onChange={handleChange('password')}
        error={Boolean(errors.password)}
        helperText={errors.password ? t(errors.password) : undefined}
        disabled={loading}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        sx={{ mt: 3, mb: 2 }}
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
      >
        {loading ? t('auth.login.submitting') : t('auth.login.submit')}
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Link
          component={RouterLink}
          to={AUTH_ROUTES.FORGOT_PASSWORD}
          underline="hover"
          sx={{ fontWeight: 'fontWeightMedium' }}
        >
          {t('auth.login.forgotPassword')}
        </Link>
      </Box>
    </Box>
  );
}
